import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDropzone } from "@/components/FileTools";
import { Trash2, Pencil, Clock, CheckCircle2, XCircle, AlertTriangle, X } from "lucide-react";
import { formatMoney, t } from "@/i18n";
import { useApp } from "@/context/AppContext";
import { PROPERTY_TYPES, propertyTypeLabel } from "@/lib/propertyTypes";
import { logWarn } from "@/lib/logger";

const STATUS_META = {
  pending:  { label: "IN ATTESA", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Clock },
  approved: { label: "PUBBLICATA", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  rejected: { label: "RIFIUTATA",  cls: "bg-red-100 text-red-800 border-red-200", Icon: XCircle },
};

const EDIT_FIELDS = {
  title: "", description: "", location: "", city: "", country: "",
  price: "", bedrooms: "", bathrooms: "", area_sqm: "",
  commission_pct: "3", property_type: "villa",
};

const toEditForm = (p) => ({
  title: p.title ?? "",
  description: p.description ?? "",
  location: p.location ?? "",
  city: p.city ?? "",
  country: p.country ?? "",
  price: p.price ?? "",
  bedrooms: p.bedrooms ?? 0,
  bathrooms: p.bathrooms ?? 0,
  area_sqm: p.area_sqm ?? 0,
  commission_pct: p.commission_pct ?? 3,
  property_type: p.property_type ?? "villa",
});

const inputCls = "bg-white border-[#E5E5E3] text-[#1A1A1A] focus-visible:ring-[#B8965A]";

export default function MyListings() {
  const { lang } = useApp();
  const [items, setItems] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EDIT_FIELDS);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/properties/mine");
      setItems(data);
    } catch (e) { logWarn("mine load failed", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/properties/${confirm.id}`);
      toast.success("Inserzione eliminata");
      setConfirm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm(toEditForm(p));
  };

  const closeEdit = () => {
    setEditing(null);
    setForm(EDIT_FIELDS);
  };

  const removeImage = async (fileId) => {
    if (!editing) return;
    try {
      const { data } = await api.delete(`/properties/${editing.id}/images/${fileId}`);
      setEditing(data);
      // reflect in the list too
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      toast.success("Foto rimossa");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const addImage = async ({ file_id }) => {
    if (!editing || !file_id) return;
    try {
      const fd = new FormData();
      fd.append("file_id", file_id);
      const { data } = await api.post(`/properties/${editing.id}/images`, fd);
      setEditing(data);
      setItems((prev) => prev.map((it) => (it.id === data.id ? data : it)));
      toast.success("Foto aggiunta");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const body = {
        title: form.title,
        description: form.description,
        location: form.location,
        city: form.city,
        country: form.country,
        price: parseFloat(form.price) || 0,
        bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        area_sqm: parseFloat(form.area_sqm) || 0,
        commission_pct: parseFloat(form.commission_pct) || 3,
        property_type: form.property_type || "villa",
      };
      await api.patch(`/properties/${editing.id}`, body);
      const wasRejected = editing.status === "rejected";
      toast.success(wasRejected ? "Modifiche salvate — l'inserzione torna in revisione" : "Modifiche salvate");
      closeEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-white border-[#E5E5E3] p-6" data-testid="my-listings">
      <div className="mb-5">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-1">Area agente</p>
        <h3 className="text-2xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          Le mie inserzioni
        </h3>
        <p className="text-xs text-[#6B6B6B] mt-1">Le nuove inserzioni sono visibili sulla bacheca solo dopo l'approvazione dell'admin.</p>
      </div>

      {!items.length && <p className="text-sm text-[#6B6B6B] py-10 text-center">Non hai ancora pubblicato inserzioni.</p>}
      <div className="space-y-3">
        {items.map((p) => {
          const s = STATUS_META[p.status] || STATUS_META.pending;
          return (
            <div key={p.id} data-testid={`my-listing-${p.id}`} className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                {p.property_type && (
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-1">
                    {propertyTypeLabel(p.property_type, lang)}
                  </p>
                )}
                <p className="text-sm font-medium text-[#1A1A1A] truncate">{p.title}</p>
                <p className="text-xs text-[#6B6B6B]">{p.city}, {p.country} · <span className="text-[#B8965A]">{formatMoney(p.price, "it")}</span></p>
                {p.status === "rejected" && p.rejection_reason && (
                  <div className="mt-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1 flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>{p.rejection_reason}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <Badge className={`text-[10px] border ${s.cls}`}><s.Icon className="h-3 w-3 me-1" />{s.label}</Badge>
                <Button size="sm" onClick={() => openEdit(p)} data-testid={`my-listing-edit-${p.id}`} className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" onClick={() => setConfirm(p)} data-testid={`my-listing-delete-${p.id}`} className="bg-transparent border border-red-300 text-red-700 hover:bg-red-50 rounded-full">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="bg-white border-[#E5E5E3] max-w-sm" data-testid="my-listing-confirm-delete">
          <DialogHeader>
            <DialogTitle className="text-xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {t("confirm_delete_listing", lang)}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B6B6B]">"{confirm?.title}"</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} className="text-[#6B6B6B]">{t("cancel", lang)}</Button>
            <Button onClick={remove} data-testid="my-listing-confirm-yes" className="bg-red-700 hover:bg-red-800 text-white rounded-full">
              {t("delete", lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && closeEdit()}>
        <DialogContent className="bg-white border-[#E5E5E3] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="my-listing-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {t("edit_listing", lang)}
            </DialogTitle>
          </DialogHeader>
          {editing?.status === "rejected" && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Al salvataggio, l'inserzione tornerà in stato "IN ATTESA" per una nuova revisione da parte dell'admin.
            </p>
          )}
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-[#4A4A4A]">Titolo</Label>
              <Input data-testid="edit-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Descrizione</Label>
              <Textarea data-testid="edit-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#4A4A4A]">Città</Label>
                <Input data-testid="edit-city" className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label className="text-[#4A4A4A]">Paese</Label>
                <Input data-testid="edit-country" className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-[#4A4A4A]">Indirizzo / zona</Label>
              <Input data-testid="edit-location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-[#4A4A4A]">Prezzo (€)</Label>
                <Input data-testid="edit-price" className={inputCls} type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <Label className="text-[#4A4A4A]">Camere</Label>
                <Input data-testid="edit-bedrooms" className={inputCls} type="number" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} />
              </div>
              <div>
                <Label className="text-[#4A4A4A]">Bagni</Label>
                <Input data-testid="edit-bathrooms" className={inputCls} type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} />
              </div>
              <div>
                <Label className="text-[#4A4A4A]">Area (m²)</Label>
                <Input data-testid="edit-area" className={inputCls} type="number" value={form.area_sqm} onChange={(e) => setForm({ ...form, area_sqm: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#4A4A4A]">Tipo di immobile</Label>
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger data-testid="edit-property-type" className={inputCls}>
                    <SelectValue placeholder="Seleziona il tipo…" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((pt) => (
                      <SelectItem key={pt.value} value={pt.value}>{pt[lang] || pt.it}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#4A4A4A]">Commissione (%)</Label>
                <Input data-testid="edit-commission" className={inputCls} type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} />
              </div>
            </div>

            {/* Photo management */}
            <div className="border-t border-[#E5E5E3] pt-4 mt-2">
              <Label className="text-[#4A4A4A] mb-3 block">Foto ({editing?.image_paths?.length || 0})</Label>
              {editing?.image_paths?.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3" data-testid="edit-photos-grid">
                  {editing.image_paths.map((fid) => (
                    <div key={fid} className="relative group aspect-square rounded overflow-hidden border border-[#E5E5E3]">
                      <img src={fileUrl(fid)} alt="" className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(fid)}
                        data-testid={`remove-photo-${fid}`}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Rimuovi foto"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <FileDropzone
                testId="edit-add-photo"
                kind="image"
                accept="image/*"
                label={t("add_more_photos", lang)}
                onUploaded={addImage}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEdit} className="text-[#6B6B6B]">{t("cancel", lang)}</Button>
            <Button
              onClick={saveEdit}
              disabled={saving || !form.title || !form.city}
              data-testid="my-listing-edit-save"
              className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full"
            >
              {saving ? t("saving", lang) : t("save_changes", lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
